import { useCallback, useEffect, useRef, useState } from 'react';

import { useElements } from '@stripe/react-stripe-js';
import type {
	StripeCardCvcElementChangeEvent,
	StripeCardExpiryElementChangeEvent,
	StripeCardNumberElementChangeEvent,
	StripeError,
} from '@stripe/stripe-js';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { PaymentType, StripeValidationErrors } from '#src/app/shared/utils/payments';
import {
	DEFAULT_STRIPE_ERROR_VALIDATION,
	focusInvalidStripeElement,
	setPxDeclineCode,
	stripeErrorI18nKey,
	stripeInputValidationError,
} from '#src/app/shared/utils/payments';

import { hasGatewayErrorData } from '../../../util/hasGatewayErrorData';

import { getUpdatedValidationErrors } from './utils/getUpdatedValidationErrors';
import { mapGatewayErrorToStripeError } from './utils/mapGatewayErrorToStripeError';

type Params = {
	disabled?: boolean;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
};

export type StripeElementChangeEvent =
	| StripeCardNumberElementChangeEvent
	| StripeCardExpiryElementChangeEvent
	| StripeCardCvcElementChangeEvent;

type Result = {
	data: {
		validationErrors: StripeValidationErrors;
		errorMessage?: string;
		errorType?: string;
	};
	actions: {
		onStripeError: (error: StripeError) => void;
		handleSubmitFailure: (error: unknown) => void;
		validateStripeElementOnChange: (e: StripeElementChangeEvent) => void;
		resetErrors: () => void;
		setValidationErrors: React.Dispatch<React.SetStateAction<StripeValidationErrors>>;
	};
};

export function useStripeFormErrors({ disabled, onTokenError, onTokenInvalid }: Params): Result {
	const { typedTranslate } = useI18n();
	const elements = useElements();
	const [validationErrors, setValidationErrors] = useState<StripeValidationErrors>(DEFAULT_STRIPE_ERROR_VALIDATION);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [errorType, setErrorType] = useState<string | undefined>(undefined);
	const focusPendingErrorsRef = useRef<StripeValidationErrors | null>(null);

	useEffect(() => {
		if (!focusPendingErrorsRef.current || disabled) return;
		focusInvalidStripeElement(elements, focusPendingErrorsRef.current);
		focusPendingErrorsRef.current = null;
	}, [validationErrors, disabled, elements]);

	const onStripeError = useCallback(
		(error: StripeError) => {
			const inputValidationErrors = stripeInputValidationError(error);

			if (inputValidationErrors) {
				focusPendingErrorsRef.current = inputValidationErrors;
				setValidationErrors(inputValidationErrors);
			} else {
				setErrorType(error.type);
				const errorMessageTranslationKey = stripeErrorI18nKey(error);
				if (errorMessageTranslationKey) setErrorMessage(typedTranslate(errorMessageTranslationKey));
				else setErrorMessage(error.message);
			}
			setPxDeclineCode(error);

			onTokenInvalid('creditCard', new Error(error.message || 'Unhandled Error'));
		},
		[onTokenInvalid, typedTranslate],
	);

	const handleSubmitFailure = useCallback(
		(error: unknown) => {
			if (hasGatewayErrorData(error)) {
				onStripeError(mapGatewayErrorToStripeError(error));
			} else {
				onTokenError('creditCard', error as Error);
			}
		},
		[onTokenError, onStripeError],
	);

	return {
		data: {
			validationErrors,
			errorMessage,
			errorType,
		},
		actions: {
			onStripeError,
			handleSubmitFailure,
			validateStripeElementOnChange: (e) => setValidationErrors((prev) => getUpdatedValidationErrors(prev, e)),
			resetErrors: () => {
				setErrorMessage(undefined);
				setErrorType(undefined);
			},
			setValidationErrors,
		},
	};
}
